import { MenuItem, Select, Checkbox, ListItemText, SelectChangeEvent, ListItemIcon, Avatar } from "@mui/material";
import { MenuProps, useStyles } from "./NewMainSelect.styles";
import { IMainSelect } from "./NewMainSelectInterfaces";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MainTag from "components/MainTag";
import { useCallback, useMemo, useState } from "react";
import { allElements, notSelected } from "constants/constants";
import { SearchBar } from "components/SearchBar/SearchBar.view";
import { t } from "i18next";

const NewMainSelect = ({
    label,
    onChange,
    value,
    options,
    small,
    disabled,
    multiple,
    className,
    tagClassName,
    size,
    fullWidth = true,
    placeholder,
    labelId,
    name,
    renderValue,
    disableMenuPropsStyles,
    withSearchBar,
    withAvatar,
    searchBarClassName,
    notSelectedOption,
    order,
}: IMainSelect) => {
    const classes = useStyles();
    const [searchText, setSearchText] = useState("");

    const sortedOptions = useMemo(() => {
        if (!order) return options;
        const copy = [...options].sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" }),
        );
        return order === "desc" ? copy.reverse() : copy;
    }, [options, order]);

    const isAllSelected = useMemo(() => {
        if (multiple && value && Array.isArray(value)) {
            return options.length > 0 && value.length === options.length;
        }
        return false;
    }, [value, options, multiple]);

    const handleChangeSingle = useCallback(
        (event: SelectChangeEvent<string | number>) => {
            if (!multiple) onChange(event.target.value);
        },
        [multiple, onChange],
    );

    const handleChangeMultiple = useCallback(
        (event: SelectChangeEvent<(string | number)[]>) => {
            const newValue = event.target.value as (string | number)[];
            if (newValue[newValue.length - 1] === allElements && multiple) {
                onChange(value!.length === options.length ? [] : [...options.map((option) => option.id)]);
                return;
            }
            if (newValue[newValue.length - 1] === notSelected && multiple) {
                onChange([notSelected]);
                return;
            }
            if (multiple) {
                const valuesWihoutNotSelected = newValue.filter((value) => value !== notSelected);
                onChange(valuesWihoutNotSelected);
            }
        },
        [multiple, onChange, options, value],
    );

    return (
        <div
            className={classes.selectWrapper}
            style={{ width: fullWidth ? "100%" : "initial", ...(fullWidth && { maxWidth: "initial" }) }}
        >
            {!!label && <span className={small ? classes.smallLabel : classes.label}>{label}</span>}
            {!multiple && (
                <Select
                    name={name}
                    labelId={labelId}
                    displayEmpty={!!placeholder}
                    disabled={disabled}
                    size={size}
                    placeholder={placeholder}
                    IconComponent={KeyboardArrowDownIcon}
                    className={`${classes.select} ${(small && classes.smallSelect) || ""} ${
                        fullWidth ? classes.fullWidth : ""
                    } ${className || ""}`}
                    value={value}
                    onChange={handleChangeSingle}
                    renderValue={(selected) => {
                        if (!selected || !value) {
                            return <span>{placeholder}</span>;
                        }

                        if (withAvatar) {
                            const option = options.find((option) => {
                                let optionValue = option.id;
                                let selectedValue = selected;
                                if (typeof optionValue === "string") {
                                    optionValue = Number(optionValue);
                                }
                                if (typeof selectedValue === "string") {
                                    selectedValue = Number(selectedValue);
                                }
                                return optionValue === selectedValue;
                            });
                            return (
                                <div className={classes.singleSelectRenderValue}>
                                    <Avatar src={option?.picture} alt="user avatar" className={classes.avatar} />
                                    <span>{option?.name}</span>
                                </div>
                            );
                        }

                        if (renderValue) {
                            return renderValue(selected);
                        }

                        return options?.find((option) => option.id === selected)?.name || "";
                    }}
                >
                    {withSearchBar && (
                        <SearchBar
                            setSearchQuery={setSearchText}
                            placeholder={t("main_select.search_placeholder")}
                            className={`${classes.searchBarClassName} ${searchBarClassName || ""}`}
                        />
                    )}
                    {sortedOptions?.map((option) => (
                        <MenuItem
                            key={option.id}
                            value={option.id}
                            className={classes.menuItem}
                            style={{
                                ...(!option.name.toLowerCase().includes(searchText.toLowerCase()) && {
                                    display: "none",
                                }),
                            }}
                        >
                            {!!withAvatar && (
                                <Avatar src={option.picture} alt={option.name} className={classes.avatar} />
                            )}

                            {!!option.tag && (
                                <ListItemText className={`${classes.menuItemWithIcon} ${classes.menuItemWithTag}`}>
                                    {!!option.icon && option.icon}
                                    {option.name}
                                    {option.tag && (
                                        <MainTag small className={classes.menuItemTag}>
                                            {option.tag}
                                        </MainTag>
                                    )}
                                </ListItemText>
                            )}

                            {!option.tag && (
                                <ListItemText className={classes.menuItemWithIcon}>
                                    {!!option.icon && option.icon}
                                    {option.name}
                                </ListItemText>
                            )}
                        </MenuItem>
                    ))}
                </Select>
            )}
            {multiple && (
                <Select
                    name={name}
                    labelId={labelId}
                    displayEmpty
                    size={size}
                    multiple
                    disabled={disabled}
                    MenuProps={disableMenuPropsStyles ? {} : MenuProps}
                    IconComponent={KeyboardArrowDownIcon}
                    className={`${classes.select} ${(small && classes.smallSelect) || ""} ${classes.selectMultiple} ${
                        className || ""
                    }`}
                    style={{ width: fullWidth ? "100%" : "initial" }}
                    renderValue={(selected) => {
                        if (selected.length === 0) {
                            return <span>{placeholder}</span>;
                        }

                        if (renderValue) {
                            return renderValue(selected);
                        }
                        const selectedItemsJoined = Array.isArray(selected)
                            ? selected
                                  .map(
                                      (select) =>
                                          options.find((option) => option.id === select)?.name ||
                                          (select === notSelected ? t("main_select.not_selected") : ""),
                                  )
                                  .join(", ")
                            : selected;
                        return (
                            <div className={classes.renderValueWrapper}>
                                <span className={classes.selectedItemsJoined}>{selectedItemsJoined}</span>
                                {!!selected.length && <span className={classes.counterBox}>{selected.length}</span>}
                            </div>
                        );
                    }}
                    value={value}
                    onChange={handleChangeMultiple}
                >
                    {withSearchBar && (
                        <SearchBar
                            placeholder={t("main_select.search_placeholder")}
                            setSearchQuery={setSearchText}
                            className={`${classes.searchBarClassName} ${searchBarClassName || ""}`}
                        />
                    )}
                    <MenuItem value={allElements} onClick={() => setSearchText("")}>
                        <ListItemIcon>
                            <Checkbox checked={isAllSelected} color="info" />
                        </ListItemIcon>
                        <ListItemText primary={t("main_select.check_all")} />
                    </MenuItem>
                    {notSelectedOption && (
                        <MenuItem value={notSelected} onClick={() => setSearchText("")}>
                            <ListItemIcon>
                                <Checkbox
                                    checked={
                                        isAllSelected ||
                                        (Array.isArray(value)
                                            ? value.some((element) => element === notSelected)
                                            : false)
                                    }
                                    color="info"
                                />
                            </ListItemIcon>
                            <ListItemText primary={t("main_select.not_selected")} />
                        </MenuItem>
                    )}
                    {sortedOptions?.map((option) => (
                        <MenuItem
                            key={option.id}
                            value={option.id}
                            className={classes.menuItem}
                            style={{
                                ...(!option.name.toLowerCase().includes(searchText.toLowerCase()) && {
                                    display: "none",
                                }),
                            }}
                        >
                            <Checkbox
                                color="info"
                                checked={Array.isArray(value) ? value.some((element) => element === option.id) : false}
                            />
                            {!!withAvatar && (
                                <Avatar src={option.picture} alt={option.name} className={classes.avatar} />
                            )}

                            {!!option.tag && (
                                <ListItemText className={classes.menuItemWithTag}>
                                    {option.name}
                                    {option.tag && (
                                        <MainTag small className={classes.menuItemTag}>
                                            {option.tag}
                                        </MainTag>
                                    )}
                                </ListItemText>
                            )}

                            {!option.tag && <ListItemText>{option.name}</ListItemText>}
                        </MenuItem>
                    ))}
                </Select>
            )}
        </div>
    );
};

export default NewMainSelect;
